import { useState, useEffect } from 'react';
import { 
  Zap, Target, Trophy, TrendingUp, Users, Crown, 
  ChevronDown, ChevronRight, Star, Shield, Clock, 
  Bell, Heart, CheckCircle, ArrowRight, Menu, X,
  Smartphone, Mail, RectangleVertical, Activity,
  Calendar, RefreshCw, ArrowLeft, BarChart3, Share2
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import IOSWaitlistBanner from '../components/IOSWaitlistBanner';
import { LockedPreview } from '../components/LockedPreview';
import { trackEvent } from '../utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [leagueTable, setLeagueTable] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/api/standings/table/Premier%20League/England`)
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setLeagueTable(data.slice(0, 5));
        }
      })
      .catch(() => {});
  }, []);

  const features = [
    {
      icon: Zap,
      title: "AI-Powered Predictions",
      description: "Our advanced AI analyzes thousands of data points to deliver accurate match predictions with confidence scores."
    },
    {
      icon: Target,
      title: "BTTS & Over 2.5",
      description: "Get probabilities for Both Teams To Score and Over 2.5 goals - the bets that matter most."
    },
    {
      icon: Trophy,
      title: "Daily Accumulators",
      description: "VIP members receive curated 4-fold accumulators and weekend specials every day."
    },
    {
      icon: TrendingUp,
      title: "Banker of the Day",
      description: "Our AI's highest confidence pick of the day - the safest bet on the slate."
    },
    {
      icon: Bell,
      title: "Live Score Alerts",
      description: "Follow any match and get instant notifications for kick-off, goals, half-time and full-time. Never miss a moment."
    },
    {
      icon: Heart,
      title: "Live Match Tracker",
      description: "Full in-app match tracker with live scores, events timeline, bookings, substitutions and real-time statistics."
    },
    {
      icon: BarChart3,
      title: "Live League Tables",
      description: "View up-to-date league standings for every domestic league. See positions, points, form, and goal difference — right next to your matches."
    },
    {
      icon: Share2,
      title: "Share Your Picks",
      description: "Share any prediction to WhatsApp, Twitter, Facebook, Telegram or Instagram. Show your mates what SmartKick's AI is backing."
    }
  ];

  const stats = [
    { value: "75%+", label: "High Confidence Picks" },
    { value: "50+", label: "Leagues Covered" },
    { value: "LIVE", label: "Score Updates" },
    { value: "24/7", label: "AI Analysis" }
  ];

  const testimonials = [
    {
      name: "James M.",
      location: "Manchester",
      text: "SmartKick has completely changed how I approach my weekend accumulators. The AI reasoning helps me understand why certain picks are made.",
      rating: 5
    },
    {
      name: "Sarah T.",
      location: "London", 
      text: "The Banker of the Day feature is brilliant. It's my go-to for that safe bet to anchor my multiples.",
      rating: 5
    },
    {
      name: "David K.",
      location: "Birmingham",
      text: "Love the BTTS predictions - they've been spot on more often than not. VIP membership is worth every penny.",
      rating: 5
    }
  ];

  const faqs = [
    {
      question: "How does SmartKick generate predictions?",
      answer: "Our AI analyzes historical match data, team form, head-to-head records, player statistics, and dozens of other factors to generate probability-based predictions. We use advanced machine learning models trained on years of football data."
    },
    {
      question: "What's included in the Pro membership?",
      answer: "Pro membership (£19.99/month) gives you access to all AI predictions, confidence scores, BTTS and Over 2.5 probabilities, match analysis, favorite teams feature, and results tracking."
    },
    {
      question: "What extra features do VIP members get?",
      answer: "VIP members (£29.99/month) get everything in Pro plus our Top 5 High Confidence Picks, Banker of the Day, Daily 4-Fold Accumulators, Weekend Specials, Daily Digest Emails, Early Access to Picks at 6 AM, and access to our VIP Discord community."
    },
    {
      question: "Can I cancel my subscription anytime?",
      answer: "Yes, you can cancel your subscription at any time from your profile settings. There are no long-term contracts or cancellation fees."
    },
    {
      question: "Which leagues do you cover?",
      answer: "We cover all major European leagues including the Premier League, La Liga, Bundesliga, Serie A, Ligue 1, as well as Championship, Scottish Premiership, Eredivisie, and many more - over 50 leagues in total. We also cover international fixtures including World Cup Qualifiers, UEFA Nations League, and International Friendlies."
    },
    {
      question: "How do the live score updates work?",
      answer: "Simply tap the bell icon on any match to follow it. You'll receive real-time score updates, plus notifications for kick-off, every goal, half-time and full-time. The in-app match tracker shows a full events timeline with goals, bookings, substitutions and live statistics like possession and shots."
    }
  ];

  return (
    <div className="min-h-screen bg-[#09090b] text-white font-['Manrope',sans-serif]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#09090b]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-['Chivo',sans-serif] font-bold">SMARTKICK</span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="/free-preview" className="text-emerald-400 hover:text-emerald-300 transition-colors font-medium" data-testid="nav-free-preview">Free Preview</a>
              <a href="/news" className="text-emerald-400 hover:text-emerald-300 transition-colors font-medium" data-testid="nav-news">News</a>
              <a href="/app/top10" className="text-emerald-300 hover:text-emerald-200 transition-colors font-semibold" data-testid="nav-top10">Daily 10</a>
              <a href="/live-scores" className="text-emerald-400 hover:text-emerald-300 transition-colors font-medium" data-testid="nav-live-scores">{t('nav.live_scores')}</a>
              <a href="#features" className="text-zinc-400 hover:text-white transition-colors">Features</a>
              <a href="#pricing" className="text-zinc-400 hover:text-white transition-colors">Pricing</a>
              <a href="/testimonials" className="text-zinc-400 hover:text-white transition-colors">Reviews</a>
              <a href="/blog" className="text-zinc-400 hover:text-white transition-colors">Blog</a>
              <a href="/partners" className="text-amber-400 hover:text-amber-300 transition-colors font-medium" data-testid="nav-partners">Partners</a>
              <a href="/about" className="text-zinc-400 hover:text-white transition-colors">About</a>
              <a href="/contact" className="text-zinc-400 hover:text-white transition-colors">Contact</a>
              <LanguageSwitcher />
              <a 
                href="/register"
                className="px-6 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-full font-bold transition-all shadow-[0_0_15px_rgba(16,185,129,0.4)]"
                data-testid="nav-get-started"
              >
                {t('nav.register')}
              </a>
            </div>

            <button 
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-zinc-900 border-t border-white/10">
            <div className="px-4 py-4 space-y-4">
              <a href="/free-preview" className="block text-emerald-400 hover:text-emerald-300 font-medium">Free Preview</a>
              <a href="/news" className="block text-emerald-400 hover:text-emerald-300 font-medium">News</a>
              <a href="/app/top10" className="block text-emerald-300 hover:text-emerald-200 font-semibold">Daily 10</a>
              <a href="/live-scores" className="block text-emerald-400 hover:text-emerald-300 font-medium">{t('nav.live_scores')}</a>
              <a href="#features" className="block text-zinc-400 hover:text-white">Features</a>
              <a href="#pricing" className="block text-zinc-400 hover:text-white">Pricing</a>
              <a href="/testimonials" className="block text-zinc-400 hover:text-white">Reviews</a>
              <a href="/blog" className="block text-zinc-400 hover:text-white">Blog</a>
              <a href="/partners" className="block text-amber-400 hover:text-amber-300 font-medium">Partners</a>
              <a href="/about" className="block text-zinc-400 hover:text-white">About</a>
              <a href="/contact" className="block text-zinc-400 hover:text-white">Contact</a>
              <div className="pt-2"><LanguageSwitcher /></div>
              <a href="/register" className="block px-6 py-3 bg-emerald-500 rounded-full font-bold text-center">
                {t('nav.register')}
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=1920&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#09090b] via-[#09090b]/90 to-[#09090b]"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          {/* iOS Launch Waitlist Banner */}
          <IOSWaitlistBanner />

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full mb-6">
                <Zap className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-400 text-sm font-medium">AI-Powered Predictions</span>
              </div>

              {/* Trial drum-beat banner */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/15 border border-amber-500/40 rounded-full mb-5" data-testid="hero-trial-badge">
                <Crown className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-amber-300 text-xs font-bold uppercase tracking-wider">14-day VIP trial · Cancel anytime</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Chivo',sans-serif] font-black mb-6 leading-tight">
                Smarter Bets,<br />
                <span className="text-emerald-400">Better Results</span>
              </h1>
              
              <p className="text-lg text-zinc-400 mb-8 max-w-xl mx-auto lg:mx-0">
                Join thousands of punters using AI-powered predictions to make informed betting decisions. Try full VIP access free for 14 days — daily picks, accumulators, Banker of the Day, early 6am access, and VIP Discord.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a 
                  href="/free-trial"
                  className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 rounded-full font-bold text-lg transition-all shadow-[0_0_20px_rgba(16,185,129,0.5)] flex items-center justify-center gap-2"
                  data-testid="hero-cta"
                >
                  Try VIP free for 14 days
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a 
                  href="/free-preview"
                  className="px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full font-bold text-lg transition-all flex items-center justify-center gap-2"
                  data-testid="hero-free-preview"
                >
                  Free Preview
                </a>
              </div>
              <p className="text-[12px] text-zinc-500 mt-3 text-center lg:text-left">Cancel anytime before day 15 — no charge</p>

              {/* Trust Badges */}
              <div className="flex items-center gap-6 mt-8 justify-center lg:justify-start">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-emerald-400" />
                  <span className="text-sm text-zinc-400">Secure Payments</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <span className="text-sm text-zinc-400">Cancel Anytime</span>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex items-center gap-3 mt-6 justify-center lg:justify-start flex-wrap">
                <a href="https://www.instagram.com/smartkicktips" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 hover:bg-emerald-500/20 border border-white/10 hover:border-emerald-500/50 transition-all" data-testid="social-instagram">
                  <svg className="w-5 h-5 text-pink-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  <span className="text-sm text-zinc-300 font-medium">@smartkicktips</span>
                </a>
                <a href="https://www.facebook.com/smartkick" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 hover:bg-emerald-500/20 border border-white/10 hover:border-emerald-500/50 transition-all" data-testid="social-facebook">
                  <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  <span className="text-sm text-zinc-300 font-medium">SmartKick</span>
                </a>
                <a href="https://t.me/+OL_GoJpb0OkyM2Jk" onClick={() => trackEvent('telegram_click', { source: 'landing_social' })} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 hover:bg-emerald-500/20 border border-white/10 hover:border-emerald-500/50 transition-all" data-testid="social-telegram">
                  <svg className="w-5 h-5 text-sky-400" fill="currentColor" viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                  <span className="text-sm text-zinc-300 font-medium">Telegram</span>
                </a>
                <a href="https://discord.gg/mYV4SBqxS" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 hover:bg-emerald-500/20 border border-white/10 hover:border-emerald-500/50 transition-all" data-testid="social-discord">
                  <svg className="w-5 h-5 text-indigo-400" fill="currentColor" viewBox="0 0 24 24"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z"/></svg>
                  <span className="text-sm text-zinc-300 font-medium">Discord</span>
                </a>
              </div>
            </div>

            {/* App Preview - Rendered Card */}
            <div className="relative hidden lg:block">
              <div className="relative mx-auto w-[300px]">
                {/* Phone Frame */}
                <div className="bg-zinc-900 rounded-[3rem] p-3 border border-white/20 shadow-2xl shadow-emerald-500/10">
                  <div className="bg-[#09090b] rounded-[2.5rem] overflow-hidden">
                    {/* Status Bar */}
                    <div className="h-6 bg-zinc-900 flex items-center justify-center">
                      <div className="w-20 h-4 bg-zinc-800 rounded-full"></div>
                    </div>
                    {/* App Content */}
                    <div className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-white font-bold text-sm">AI Predictions</span>
                        <span className="text-[10px] text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded-full">Today</span>
                      </div>
                      {/* Barca vs Atletico card */}
                      <div className="bg-zinc-800/60 border border-white/5 rounded-xl p-3">
                        <div className="text-[9px] text-emerald-400 uppercase tracking-wider mb-2">La Liga</div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1">
                            <img src="/badges/barcelona.png" alt="" className="w-6 h-6" />
                            <span className="text-xs text-white font-medium">Barcelona</span>
                          </div>
                          <div className="text-center px-2">
                            <span className="text-white font-mono text-xs">20:00</span>
                          </div>
                          <div className="flex items-center gap-2 flex-1 justify-end">
                            <span className="text-xs text-white font-medium">Atletico</span>
                            <img src="/badges/atletico.png" alt="" className="w-6 h-6" />
                          </div>
                        </div>
                        <div className="flex justify-between items-center mt-2 pt-2 border-t border-white/5">
                          <span className="text-[10px] text-emerald-400 font-bold">Barcelona Win</span>
                          <span className="text-[10px] text-emerald-400 bg-emerald-400/10 px-1.5 py-0.5 rounded font-bold">76%</span>
                        </div>
                        <div className="flex justify-between items-center mt-0.5">
                          <span className="text-[10px] text-zinc-500">Score: 2-1</span>
                          <span className="text-[10px] text-zinc-600 font-mono">12%</span>
                        </div>
                      </div>
                      {/* Man City vs Liverpool */}
                      <div className="bg-zinc-800/60 border border-white/5 rounded-xl p-3">
                        <div className="text-[9px] text-emerald-400 uppercase tracking-wider mb-2">Premier League</div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1">
                            <img src="/badges/arsenal.png" alt="" className="w-6 h-6" />
                            <span className="text-xs text-white font-medium">Arsenal</span>
                          </div>
                          <div className="text-center px-2">
                            <span className="text-white font-mono text-xs">17:30</span>
                          </div>
                          <div className="flex items-center gap-2 flex-1 justify-end">
                            <span className="text-xs text-white font-medium">Chelsea</span>
                            <img src="/badges/chelsea.png" alt="" className="w-6 h-6" />
                          </div>
                        </div>
                        <div className="flex justify-between items-center mt-2 pt-2 border-t border-white/5">
                          <span className="text-[10px] text-emerald-400 font-bold">Arsenal Win</span>
                          <span className="text-[10px] text-emerald-400 bg-emerald-400/10 px-1.5 py-0.5 rounded font-bold">71%</span>
                        </div>
                        <div className="flex justify-between items-center mt-1">
                          <span className="text-[10px] text-zinc-500">Score: 2-0</span>
                          <span className="text-[10px] text-zinc-600 font-mono">14%</span>
                        </div>
                      </div>
                      {/* Quick stats */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-zinc-800/60 rounded-lg p-2 text-center">
                          <span className="text-[9px] text-zinc-500 block">BTTS</span>
                          <span className="text-emerald-400 font-bold text-xs">68%</span>
                        </div>
                        <div className="bg-zinc-800/60 rounded-lg p-2 text-center">
                          <span className="text-[9px] text-zinc-500 block">Over 2.5</span>
                          <span className="text-emerald-400 font-bold text-xs">72%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating Badge */}
                <div className="absolute -right-4 top-20 bg-emerald-500 text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg animate-bounce">
                  75%+ Accuracy
                </div>
              </div>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16">
            {stats.map((stat, index) => (
              <div key={index} className="bg-zinc-900/50 border border-white/10 rounded-xl p-6 text-center backdrop-blur-sm">
                <div className="text-3xl font-['Chivo',sans-serif] font-black text-emerald-400 mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-zinc-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App Screenshots Showcase Section */}
      <section className="py-16 px-4 bg-zinc-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black mb-4">
              See It In <span className="text-emerald-400">Action</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Get daily AI-powered predictions with confidence scores, BTTS picks, and detailed match analysis.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 items-start justify-center">
            {/* Card 1 - AI Predictions (Phone Frame) */}
            <div className="mx-auto w-full max-w-[300px]">
              <div className="bg-zinc-900 rounded-[2rem] p-2 border border-emerald-500/20 shadow-[0_0_30px_rgba(16,185,129,0.08)] h-[520px] overflow-hidden">
                <div className="bg-[#0c0c0f] rounded-[1.5rem] h-full overflow-hidden p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-white font-black text-sm uppercase tracking-wide">AI Predictions</h3>
                    <RefreshCw className="w-4 h-4 text-zinc-600" />
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-zinc-500">
                    <Calendar className="w-3 h-3" /> Saturday 5 April
                  </div>
                  <div className="flex gap-1.5">
                    <span className="text-[10px] bg-emerald-500 text-black font-bold px-3 py-1 rounded-full">Today</span>
                    <span className="text-[10px] bg-zinc-800 text-zinc-400 px-3 py-1 rounded-full">Tomorrow</span>
                    <span className="text-[10px] bg-zinc-800 text-zinc-400 px-3 py-1 rounded-full">All</span>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    <div className="bg-zinc-800/60 rounded-lg p-2 text-center"><span className="text-white font-bold text-sm block">23</span><span className="text-[9px] text-zinc-500">Matches</span></div>
                    <div className="bg-zinc-800/60 rounded-lg p-2 text-center"><span className="text-emerald-400 font-bold text-sm block">5</span><span className="text-[9px] text-zinc-500">High Conf</span></div>
                    <div className="bg-zinc-800/60 rounded-lg p-2 text-center"><span className="text-white font-bold text-sm block">15</span><span className="text-[9px] text-zinc-500">Leagues</span></div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] text-emerald-400 font-bold uppercase">High Confidence</span>
                      <span className="text-[9px] text-zinc-600">65%+ AI Confidence</span>
                    </div>
                    <div className="space-y-1.5">
                      {[
                        { league: 'La Liga', match: 'Barcelona vs Atletico', pick: 'Barcelona', conf: 76 },
                        { league: 'Premier League', match: 'Arsenal vs Chelsea', pick: 'Arsenal', conf: 71 },
                        { league: 'Serie A', match: 'Juventus vs AC Milan', pick: 'Juventus', conf: 68 },
                      ].map((m, i) => (
                        <div key={i} className="bg-zinc-800/40 rounded-lg p-2">
                          <span className="text-[9px] text-emerald-400/60 block">{m.league}</span>
                          <span className="text-[10px] text-white block">{m.match}</span>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[9px] text-zinc-500">Pick: {m.pick}</span>
                            <div className="flex items-center gap-1.5">
                              <div className="h-1 w-12 bg-zinc-700 rounded-full"><div className="h-full bg-emerald-500 rounded-full" style={{width: `${m.conf}%`}} /></div>
                              <span className="text-[10px] text-emerald-400 font-bold">{m.conf}%</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] text-emerald-400 font-bold uppercase">BTTS Likely</span>
                      <span className="text-[9px] text-zinc-600">60%+ Both Teams Score</span>
                    </div>
                    <div className="bg-zinc-800/40 rounded-lg p-2">
                      <span className="text-[9px] text-emerald-400/60 block">La Liga</span>
                      <span className="text-[10px] text-white block">Barcelona vs Atletico</span>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[9px] text-zinc-500">Both Teams To Score</span>
                        <span className="text-[10px] text-emerald-400 font-bold">68%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 text-center">
                <h3 className="font-bold text-white mb-1">Daily Predictions</h3>
                <p className="text-sm text-zinc-500">AI confidence scores for every match</p>
              </div>
            </div>
            
            {/* Card 2 - Match Analysis (Phone Frame) */}
            <div className="mx-auto w-full max-w-[300px]">
              <div className="bg-zinc-900 rounded-[2rem] p-2 border border-emerald-500/20 shadow-[0_0_30px_rgba(16,185,129,0.08)] h-[520px] overflow-hidden">
                <div className="bg-[#0c0c0f] rounded-[1.5rem] h-full overflow-hidden p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <ArrowLeft className="w-4 h-4 text-zinc-500" />
                    <span className="text-xs text-zinc-500">Back</span>
                  </div>
                  <div className="flex items-center justify-center gap-1 text-[10px] text-zinc-500">
                    <span className="text-emerald-400 uppercase font-bold">La Liga</span>
                  </div>
                  <div className="flex items-center gap-1 justify-center text-[10px] text-zinc-500">
                    <Clock className="w-3 h-3" /> Sat 5 Apr &bull; 20:00
                  </div>
                  <div className="flex items-center justify-between px-2">
                    <div className="text-center">
                      <img src="/badges/barcelona.png" alt="" className="w-12 h-12 object-contain mx-auto mb-1" />
                      <span className="text-[10px] text-white font-medium block">Barcelona</span>
                      <span className="text-[9px] text-zinc-600">Home</span>
                    </div>
                    <span className="text-zinc-600 font-bold text-lg">VS</span>
                    <div className="text-center">
                      <img src="/badges/atletico.png" alt="" className="w-12 h-12 object-contain mx-auto mb-1" />
                      <span className="text-[10px] text-white font-medium block">Atletico</span>
                      <span className="text-[9px] text-zinc-600">Away</span>
                    </div>
                  </div>
                  <div className="bg-zinc-800/40 rounded-xl p-3 text-center">
                    <span className="text-[9px] text-emerald-400 uppercase font-bold block mb-2">Favourite to Win</span>
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <img src="/badges/barcelona.png" alt="" className="w-6 h-6 object-contain" />
                      <span className="text-white font-bold text-sm">Barcelona</span>
                    </div>
                    <span className="text-emerald-400 font-black text-2xl">48%</span>
                    <span className="text-[9px] text-zinc-500 block mt-0.5">Win Probability</span>
                  </div>
                  <div className="space-y-1.5">
                    {[
                      { team: 'Barcelona', pct: 48, color: 'bg-emerald-500' },
                      { team: 'Draw', pct: 26, color: 'bg-amber-500' },
                      { team: 'Atletico', pct: 26, color: 'bg-blue-500' },
                    ].map((r, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-[10px] text-zinc-500 w-16 text-right">{r.team}</span>
                        <div className="flex-1 h-1.5 bg-zinc-800 rounded-full"><div className={`h-full ${r.color} rounded-full`} style={{width: `${r.pct}%`}} /></div>
                        <span className="text-[10px] text-white font-bold w-8">{r.pct}%</span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-zinc-800/30 rounded-lg p-2 flex items-center justify-between">
                    <div className="text-left">
                      <span className="text-[9px] text-zinc-500 uppercase block">Correct Score</span>
                      <span className="text-white font-bold text-xs">2 - 1</span>
                    </div>
                    <span className="text-zinc-400 font-mono text-xs bg-zinc-800/60 px-2 py-0.5 rounded">12%</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 text-center">
                <h3 className="font-bold text-white mb-1">Match Analysis</h3>
                <p className="text-sm text-zinc-500">In-depth AI predictions with score forecasts</p>
              </div>
            </div>
            
            {/* Card 3 - Best Odds / VIP Acca (Phone Frame) */}
            <div className="mx-auto w-full max-w-[300px]">
              <div className="bg-zinc-900 rounded-[2rem] p-2 border border-emerald-500/20 shadow-[0_0_30px_rgba(16,185,129,0.08)] h-[520px] overflow-hidden">
                <div className="bg-[#0c0c0f] rounded-[1.5rem] h-full overflow-hidden p-4 space-y-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Crown className="w-4 h-4 text-amber-400" />
                    <span className="text-white font-black text-sm uppercase tracking-wide">Best Odds Comparison</span>
                    <span className="text-[8px] bg-emerald-500 text-black font-bold px-1.5 py-0.5 rounded ml-auto">VIP</span>
                  </div>
                  <span className="text-[10px] text-zinc-500 block">8 bookmakers compared</span>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { team: 'Barcelona', odds: '4/7', bookie: 'Bet365' },
                      { team: 'Draw', odds: '3/1', bookie: 'Betfair' },
                      { team: 'Atletico', odds: '4/1', bookie: 'Smarkets' },
                    ].map((o, i) => (
                      <div key={i} className="bg-zinc-800/40 rounded-lg p-2 text-center">
                        <span className="text-[9px] text-zinc-500 block">{o.team}</span>
                        <span className="text-emerald-400 font-bold text-base block">{o.odds}</span>
                        <span className="text-[8px] text-zinc-600">{o.bookie}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-1 text-[10px] text-emerald-400">
                    <ChevronRight className="w-3 h-3" /> View all bookmakers (8)
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'Both Teams Score', val: '68%' },
                      { label: 'Over 2.5 Goals', val: '72%' },
                      { label: 'Over 2.5 Cards', val: '62%' },
                      { label: 'Over 8.5 Corners', val: '58%' },
                      { label: 'Over 3.5 Shots OT', val: '65%' },
                      { label: 'Over 20 Fouls', val: '54%' },
                    ].map((s, i) => (
                      <div key={i} className="bg-zinc-800/40 rounded-lg p-2 text-center">
                        <span className="text-[9px] text-zinc-500 block mb-0.5">{s.label}</span>
                        <span className={`font-bold text-sm ${parseInt(s.val) >= 60 ? 'text-emerald-400' : 'text-amber-400'}`}>{s.val}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <span className="text-[10px] text-zinc-500 uppercase font-bold block mb-1.5">Player Predictions</span>
                    <div className="flex items-center gap-2 bg-zinc-800/40 rounded-lg p-2">
                      <img src="/players/lewandowski.png" alt="" className="w-7 h-7 rounded-full object-cover border border-white/10" />
                      <div className="flex-1 min-w-0">
                        <span className="text-[10px] text-white font-medium block truncate">R. Lewandowski</span>
                        <span className="text-[8px] text-zinc-500">Barcelona - To Be Carded</span>
                      </div>
                      <span className="text-emerald-400 font-bold text-xs">45%</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 text-center">
                <h3 className="font-bold text-white mb-1">Best Odds Finder</h3>
                <p className="text-sm text-zinc-500">Compare odds from 8+ bookmakers instantly</p>
              </div>
            </div>
            
            {/* Card 4 - Player Predictions (Phone Frame) */}
            <div className="mx-auto w-full max-w-[300px]">
              <div className="bg-zinc-900 rounded-[2rem] p-2 border border-emerald-500/20 shadow-[0_0_30px_rgba(16,185,129,0.08)] h-[520px] overflow-hidden">
                <div className="bg-[#0c0c0f] rounded-[1.5rem] h-full overflow-hidden p-4 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-black text-sm uppercase tracking-wide">Player Predictions</span>
                    <span className="text-[10px] text-zinc-500">7 picks</span>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-zinc-500">
                    <img src="/badges/barcelona.png" alt="" className="w-4 h-4" /> Barcelona vs Atletico Madrid
                  </div>
                  <div>
                    <span className="text-[9px] text-zinc-500 uppercase font-bold block mb-1.5">Cards</span>
                    <div className="space-y-1.5">
                      {[
                        { name: 'Pedri', team: 'Barcelona', pred: 'To Be Carded', conf: 55, photo: '/players/pedri.png' },
                        { name: 'A. Griezmann', team: 'Atletico', pred: 'To Be Carded', conf: 48, photo: '/players/griezmann.png' },
                      ].map((p, i) => (
                        <div key={i} className="flex items-center gap-2 bg-zinc-800/40 rounded-lg p-2">
                          <img src={p.photo} alt="" className="w-7 h-7 rounded-full object-cover border border-white/10" />
                          <div className="flex-1 min-w-0">
                            <span className="text-[10px] text-white font-medium block truncate">{p.name}</span>
                            <span className="text-[8px] text-zinc-500">{p.team} - {p.pred}</span>
                          </div>
                          <span className={`font-bold text-xs ${p.conf >= 50 ? 'text-emerald-400' : 'text-amber-400'}`}>{p.conf}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="text-[9px] text-zinc-500 uppercase font-bold block mb-1.5">Shots on Target</span>
                    <div className="space-y-1.5">
                      {[
                        { name: 'Lamine Yamal', team: 'Barcelona', pred: '1+ Shots on Target', conf: 72, photo: '/players/yamal.png' },
                        { name: 'J. Alvarez', team: 'Atletico', pred: '1+ Shots on Target', conf: 58, photo: '/players/alvarez.png' },
                      ].map((p, i) => (
                        <div key={i} className="flex items-center gap-2 bg-zinc-800/40 rounded-lg p-2">
                          <img src={p.photo} alt="" className="w-7 h-7 rounded-full object-cover border border-white/10" />
                          <div className="flex-1 min-w-0">
                            <span className="text-[10px] text-white font-medium block truncate">{p.name}</span>
                            <span className="text-[8px] text-zinc-500">{p.team} - {p.pred}</span>
                          </div>
                          <span className={`font-bold text-xs ${p.conf >= 50 ? 'text-emerald-400' : 'text-amber-400'}`}>{p.conf}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="text-[9px] text-zinc-500 uppercase font-bold block mb-1.5">Goalscorers</span>
                    <div className="space-y-1.5">
                      {[
                        { name: 'R. Lewandowski', team: 'Barcelona', pred: 'Anytime Goalscorer', conf: 55, photo: '/players/lewandowski.png' },
                        { name: 'A. Griezmann', team: 'Atletico', pred: 'Anytime Goalscorer', conf: 40, photo: '/players/griezmann.png' },
                      ].map((p, i) => (
                        <div key={i} className="flex items-center gap-2 bg-zinc-800/40 rounded-lg p-2">
                          <img src={p.photo} alt="" className="w-7 h-7 rounded-full object-cover border border-white/10" />
                          <div className="flex-1 min-w-0">
                            <span className="text-[10px] text-white font-medium block truncate">{p.name}</span>
                            <span className="text-[8px] text-zinc-500">{p.team} - {p.pred}</span>
                          </div>
                          <span className={`font-bold text-xs ${p.conf >= 50 ? 'text-emerald-400' : 'text-amber-400'}`}>{p.conf}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-zinc-800/40 rounded-lg p-2 mt-1">
                    <span className="text-[9px] text-zinc-500 uppercase font-bold block mb-1">AI Reasoning</span>
                    <p className="text-[9px] text-zinc-400 leading-relaxed">Barcelona's attacking depth with Lewandowski and Yamal makes them strong at home. Atletico's defensive discipline means cards are likely.</p>
                  </div>
                </div>
              </div>
              <div className="mt-4 text-center">
                <h3 className="font-bold text-white mb-1">Player Props</h3>
                <p className="text-sm text-zinc-500">Goalscorer, shots & cards predictions</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEW Features Highlight */}
      <section className="py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-4">
              <Zap className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-xs text-emerald-400 font-semibold uppercase tracking-wider">Just Launched</span>
            </span>
            <h2 className="text-2xl sm:text-3xl font-black text-white">New on SmartKick</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {/* League Tables Card */}
            <div className="relative overflow-hidden bg-gradient-to-br from-zinc-900 to-zinc-950 border border-emerald-500/20 rounded-2xl p-6 group hover:border-emerald-500/40 transition-all">
              <div className="absolute top-3 right-3 text-[10px] bg-emerald-500 text-black font-bold px-2 py-0.5 rounded-full">NEW</div>
              <BarChart3 className="w-10 h-10 text-emerald-400 mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Live League Tables</h3>
              <p className="text-zinc-400 text-sm mb-4">See where every team stands. Full standings for all major domestic leagues — positions, points, form, goal difference — updated daily and shown right next to your matches.</p>
              <div className="bg-zinc-900/80 rounded-lg border border-white/5 p-3">
                <div className="space-y-1.5 text-[11px]">
                  {(leagueTable.length > 0 ? leagueTable : [
                    { rank: 1, team: 'Arsenal', points: 70, goal_diff: '+39' },
                    { rank: 2, team: 'Man City', points: 61, goal_diff: '+32' },
                    { rank: 3, team: 'Man United', points: 55, goal_diff: '+13' },
                    { rank: 4, team: 'Aston Villa', points: 54, goal_diff: '+5' },
                  ]).map((t, i) => (
                    <div key={t.rank || i} className="flex items-center gap-3" data-testid={`landing-table-row-${i}`}>
                      <span className={`font-mono font-bold ${i < 4 ? 'text-emerald-400' : 'text-zinc-400'}`}>{t.rank || t.pos}</span>
                      {t.team_logo && (
                        <img src={`${API_URL}/api/proxy/team-image/${t.team_id}`} alt="" className="w-4 h-4 object-contain" />
                      )}
                      <span className="text-white flex-1 truncate">{t.team}</span>
                      <span className="text-zinc-500 font-mono">{typeof t.goal_diff === 'number' ? (t.goal_diff > 0 ? `+${t.goal_diff}` : t.goal_diff) : t.goal_diff || t.gd}</span>
                      <span className="text-white font-bold font-mono w-6 text-right">{t.points || t.pts}</span>
                    </div>
                  ))}
                  {leagueTable.length > 0 && (
                    <div className="text-[9px] text-zinc-600 text-right mt-1 pt-1 border-t border-white/5">Premier League - Updated daily</div>
                  )}
                </div>
              </div>
            </div>

            {/* Share Picks Card */}
            <div className="relative overflow-hidden bg-gradient-to-br from-zinc-900 to-zinc-950 border border-emerald-500/20 rounded-2xl p-6 group hover:border-emerald-500/40 transition-all">
              <div className="absolute top-3 right-3 text-[10px] bg-emerald-500 text-black font-bold px-2 py-0.5 rounded-full">NEW</div>
              <Share2 className="w-10 h-10 text-emerald-400 mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Share Your Picks</h3>
              <p className="text-zinc-400 text-sm mb-4">Found a pick you love? Share it instantly to your socials. Let your mates see what the AI is backing — and bring them over to SmartKick.</p>
              <div className="flex items-center gap-3 flex-wrap">
                {[
                  { name: 'WhatsApp', color: 'bg-[#25D366]/15 text-[#25D366] border-[#25D366]/30' },
                  { name: 'Twitter', color: 'bg-white/5 text-white border-white/20' },
                  { name: 'Facebook', color: 'bg-[#1877F2]/15 text-[#1877F2] border-[#1877F2]/30' },
                  { name: 'Telegram', color: 'bg-[#0088cc]/15 text-[#0088cc] border-[#0088cc]/30' },
                  { name: 'Instagram', color: 'bg-[#E4405F]/15 text-[#E4405F] border-[#E4405F]/30' },
                ].map(p => (
                  <span key={p.name} className={`text-[11px] font-medium px-3 py-1.5 rounded-full border ${p.color}`}>
                    {p.name}
                  </span>
                ))}
              </div>
              <div className="mt-4 bg-zinc-900/80 rounded-lg border border-white/5 p-3">
                <p className="text-[11px] text-zinc-500 italic">"SmartKick AI predicts Arsenal vs Chelsea — Arsenal (71% confidence). Get today's free picks..."</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Locked premium preview — real fixture, AI pick hidden behind trial */}
      <section className="px-4">
        <LockedPreview />
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-['Chivo',sans-serif] font-black mb-4">
              Everything You Need to <span className="text-emerald-400">Win</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Powered by advanced AI and real-time data, SmartKick gives you the edge you've been looking for.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-zinc-900/50 border border-white/10 rounded-xl p-6 hover:border-emerald-500/50 transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-4 group-hover:bg-emerald-500/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-zinc-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 bg-zinc-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-['Chivo',sans-serif] font-black mb-4">
              How It <span className="text-emerald-400">Works</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-['Chivo',sans-serif] font-black text-emerald-400">1</span>
              </div>
              <h3 className="text-xl font-bold mb-2">Sign Up</h3>
              <p className="text-zinc-400">Create your account in seconds and choose your subscription plan.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-['Chivo',sans-serif] font-black text-emerald-400">2</span>
              </div>
              <h3 className="text-xl font-bold mb-2">Get Predictions</h3>
              <p className="text-zinc-400">Receive AI-powered predictions for matches across 50+ leagues daily.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-['Chivo',sans-serif] font-black text-emerald-400">3</span>
              </div>
              <h3 className="text-xl font-bold mb-2">Place Smarter Bets</h3>
              <p className="text-zinc-400">Use our insights and reasoning to make informed betting decisions.</p>
            </div>
          </div>
        </div>
      </section>

      {/* NEW: Live Scores Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-500/5 to-transparent" />
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold uppercase tracking-wider mb-6">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                New Feature
              </div>
              <h2 className="text-3xl sm:text-4xl font-['Chivo',sans-serif] font-black mb-4">
                Live Score <span className="text-emerald-400">Updates</span>
              </h2>
              <p className="text-zinc-400 text-lg mb-8">
                Follow any match and never miss a moment. Get real-time score updates, goal alerts, and a full match tracker right inside SmartKick.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Bell, text: "Instant goal notifications" },
                  { icon: Clock, text: "Kick-off, half-time & full-time alerts" },
                  { icon: Shield, text: "Full events timeline - goals, cards, subs" },
                  { icon: TrendingUp, text: "Live match stats - possession, shots & more" }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 text-emerald-400" />
                    </div>
                    <span className="text-zinc-300">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Visual mock of a live match card */}
            <div className="flex justify-center">
              <div className="w-full max-w-sm">
                {/* Mock live match card */}
                <div className="bg-zinc-900/80 backdrop-blur-md border border-emerald-500/30 rounded-2xl p-6 shadow-[0_0_40px_rgba(16,185,129,0.1)]">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Premier League</span>
                    <span className="flex items-center gap-1 text-[10px] text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded-full font-bold">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE 67'
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-6">
                    <div className="text-center flex-1">
                      <img src="/badges/arsenal.png" alt="Arsenal" className="w-14 h-14 object-contain mx-auto mb-2" />
                      <span className="text-sm text-white font-medium">Arsenal</span>
                    </div>
                    <div className="text-center px-4">
                      <div className="text-4xl font-mono font-bold text-emerald-400">2 - 1</div>
                      <span className="text-xs text-emerald-400/60">67'</span>
                    </div>
                    <div className="text-center flex-1">
                      <img src="/badges/chelsea.png" alt="Chelsea" className="w-14 h-14 object-contain mx-auto mb-2" />
                      <span className="text-sm text-white font-medium">Chelsea</span>
                    </div>
                  </div>
                  {/* Mock events */}
                  <div className="space-y-2 border-t border-white/5 pt-4">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-zinc-500 font-mono w-8">12'</span>
                      <Target className="w-3 h-3 text-emerald-400" />
                      <span className="text-emerald-400 font-medium">Saka</span>
                      <span className="text-zinc-600">1-0</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-zinc-500 font-mono w-8">34'</span>
                      <Target className="w-3 h-3 text-emerald-400" />
                      <span className="text-white font-medium">Palmer</span>
                      <span className="text-zinc-600">1-1</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-zinc-500 font-mono w-8">55'</span>
                      <Target className="w-3 h-3 text-emerald-400" />
                      <span className="text-emerald-400 font-medium">Rice</span>
                      <span className="text-zinc-600">2-1</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-zinc-500 font-mono w-8">62'</span>
                      <RectangleVertical className="w-3 h-3 text-amber-400 fill-amber-400" />
                      <span className="text-white font-medium">Caicedo</span>
                    </div>
                  </div>
                  {/* AI Prediction comparison */}
                  <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between">
                    <span className="text-[10px] text-zinc-500">AI Prediction</span>
                    <span className="text-[10px] text-emerald-400 font-bold">Arsenal Win (78%)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-['Chivo',sans-serif] font-black mb-4">
              Simple, Transparent <span className="text-emerald-400">Pricing</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Choose the plan that's right for you. Cancel anytime.
            </p>
            <a
              href="/free-trial"
              className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-full bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-300 text-sm font-bold transition-all"
              data-testid="pricing-trial-cta"
            >
              <Crown className="w-4 h-4" /> Or try VIP free for 14 days — cancel anytime
            </a>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Basic Plan */}
            <div className="bg-zinc-900/50 border border-white/10 rounded-2xl p-7 relative" data-testid="basic-plan-card">
              <div className="absolute top-4 right-4 px-3 py-1 bg-sky-500/20 border border-sky-500/40 text-sky-300 rounded-full text-xs font-bold">
                STARTER
              </div>
              <h3 className="text-2xl font-['Chivo',sans-serif] font-black mb-2">Basic</h3>
              <p className="text-zinc-400 mb-6 text-sm">Just the AI picks — cheapest way in</p>
              <div className="mb-6">
                <span className="text-4xl font-['Chivo',sans-serif] font-black">£9.99</span>
                <span className="text-zinc-500">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                {[
                  "All AI match predictions",
                  "Confidence scores",
                  "Cancel anytime"
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-sky-400 flex-shrink-0" />
                    <span className="text-zinc-300">{feature}</span>
                  </li>
                ))}
              </ul>
              <a
                href="/register?plan=basic"
                className="block w-full py-4 bg-sky-500 hover:bg-sky-600 rounded-full font-bold text-center transition-all"
                data-testid="basic-plan-cta"
              >
                Get Started
              </a>
            </div>

            {/* Pro Plan */}
            <div className="bg-zinc-900/50 border border-emerald-500/40 rounded-2xl p-7 relative md:scale-[1.03]" data-testid="pro-plan-card">
              <div className="absolute top-4 right-4 px-3 py-1 bg-emerald-500 rounded-full text-xs font-bold">
                POPULAR
              </div>
              <h3 className="text-2xl font-['Chivo',sans-serif] font-black mb-2">Pro</h3>
              <p className="text-zinc-400 mb-6 text-sm">Full access to SmartKick AI</p>
              <div className="mb-6">
                <span className="text-4xl font-['Chivo',sans-serif] font-black">£19.99</span>
                <span className="text-zinc-500">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                {[
                  "Everything in Basic, plus:",
                  "BTTS & Over 2.5 predictions",
                  "Match analysis & reasoning",
                  "Favorite teams & leagues",
                  "Live score updates & match tracker",
                  "Results tracking",
                  "Push notifications"
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className={i === 0 ? "text-emerald-300 font-medium" : "text-zinc-300"}>{feature}</span>
                  </li>
                ))}
              </ul>
              <a 
                href="/register?plan=pro"
                className="block w-full py-4 bg-emerald-500 hover:bg-emerald-600 rounded-full font-bold text-center transition-all shadow-[0_0_15px_rgba(16,185,129,0.4)]"
                data-testid="pro-plan-cta"
              >
                Get Started
              </a>
            </div>

            {/* VIP Plan */}
            <div className="bg-gradient-to-b from-amber-500/10 to-zinc-900/50 border border-amber-500/30 rounded-2xl p-7 relative" data-testid="vip-plan-card">
              <div className="absolute top-4 right-4 px-3 py-1 bg-amber-500 text-black rounded-full text-xs font-bold">
                VIP
              </div>
              <h3 className="text-2xl font-['Chivo',sans-serif] font-black mb-2">VIP</h3>
              <p className="text-zinc-400 mb-6 text-sm">Premium features for serious punters</p>
              <div className="mb-6">
                <span className="text-4xl font-['Chivo',sans-serif] font-black">£29.99</span>
                <span className="text-zinc-500">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                {[
                  "Everything in Pro, plus:",
                  "Top 5 High Confidence Picks (75%+)",
                  "Banker of the Day",
                  "Daily 4-Fold Accumulator",
                  "Weekend Special Accumulators",
                  "Daily Digest Email",
                  "Early Access to Picks (6 AM)",
                  "VIP Discord Community"
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className={`w-5 h-5 flex-shrink-0 ${i === 0 ? 'text-amber-400' : 'text-emerald-400'}`} />
                    <span className={i === 0 ? 'text-amber-400 font-medium' : 'text-zinc-300'}>{feature}</span>
                  </li>
                ))}
              </ul>
              <a 
                href="/register?plan=vip"
                className="block w-full py-4 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black rounded-full font-bold text-center transition-all"
                data-testid="vip-plan-cta"
              >
                <Crown className="w-5 h-5 inline mr-2" />
                Get VIP Access
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-zinc-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-['Chivo',sans-serif] font-black mb-4">
              What Our <span className="text-emerald-400">Members</span> Say
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-zinc-900/50 border border-white/10 rounded-xl p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-zinc-300 mb-4">"{testimonial.text}"</p>
                <div>
                  <div className="font-bold">{testimonial.name}</div>
                  <div className="text-sm text-zinc-500">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-['Chivo',sans-serif] font-black mb-4">
              Frequently Asked <span className="text-emerald-400">Questions</span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-zinc-900/50 border border-white/10 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
                  data-testid={`faq-${index}`}
                >
                  <span className="font-bold pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronDown className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-zinc-500 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 text-zinc-400">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-emerald-500/20 to-emerald-600/20 border border-emerald-500/30 rounded-3xl p-8 md:p-12">
            <h2 className="text-3xl sm:text-4xl font-['Chivo',sans-serif] font-black mb-4">
              Ready to Start Winning?
            </h2>
            <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
              Join SmartKick today and get AI-powered predictions for every match. 
              Make smarter bets and track your results.
            </p>
            <a 
              href="/register"
              className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-500 hover:bg-emerald-600 rounded-full font-bold text-lg transition-all shadow-[0_0_20px_rgba(16,185,129,0.5)]"
              data-testid="final-cta"
            >
              Get Started Now
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Gambling Disclaimer */}
      <section className="py-8 px-4 bg-amber-500/10 border-t border-amber-500/30">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-amber-400 text-sm mb-3">
            <strong>18+ | Gamble Responsibly</strong>
          </p>
          <p className="text-amber-200/70 text-xs mb-4">
            SmartKick predictions are for entertainment purposes only. We do not guarantee results 
            and are not responsible for any losses. Always gamble responsibly and only bet what you can afford to lose.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-xs">
            <a href="https://www.begambleaware.org" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 underline">
              BeGambleAware.org
            </a>
            <a href="https://www.gamcare.org.uk" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 underline">
              GamCare
            </a>
            <a href="https://www.gamblingtherapy.org" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 underline">
              Gambling Therapy
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-['Chivo',sans-serif] font-bold">SMARTKICK</span>
              </div>
              <p className="text-zinc-500 text-sm">
                AI-Powered Match Predictions for smarter betting decisions.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Product</h4>
              <ul className="space-y-2 text-zinc-400 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="/blog" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="/testimonials" className="hover:text-white transition-colors">Reviews</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Company</h4>
              <ul className="space-y-2 text-zinc-400 text-sm">
                <li><a href="/about" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="/contact" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="/app/privacy" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="/app/terms" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-2 text-zinc-400 text-sm">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  support@smartkick.co.uk
                </li>
              </ul>
              <div className="flex items-center gap-3 mt-4">
                <a href="https://www.instagram.com/smartkicktips" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/5 hover:bg-emerald-500/20 border border-white/10 hover:border-emerald-500/50 flex items-center justify-center transition-all" aria-label="Instagram">
                  <svg className="w-4 h-4 text-zinc-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </a>
                <a href="https://www.facebook.com/smartkick" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/5 hover:bg-emerald-500/20 border border-white/10 hover:border-emerald-500/50 flex items-center justify-center transition-all" aria-label="Facebook">
                  <svg className="w-4 h-4 text-zinc-400" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="https://t.me/+OL_GoJpb0OkyM2Jk" onClick={() => trackEvent('telegram_click', { source: 'landing_footer' })} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/5 hover:bg-emerald-500/20 border border-white/10 hover:border-emerald-500/50 flex items-center justify-center transition-all" aria-label="Telegram">
                  <svg className="w-4 h-4 text-zinc-400" fill="currentColor" viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                </a>
                <a href="https://discord.gg/mYV4SBqxS" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/5 hover:bg-emerald-500/20 border border-white/10 hover:border-emerald-500/50 flex items-center justify-center transition-all" aria-label="Discord">
                  <svg className="w-4 h-4 text-zinc-400" fill="currentColor" viewBox="0 0 24 24"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z"/></svg>
                </a>
              </div>
              <div className="mt-4">
                <a 
                  href="/register"
                  className="inline-block px-6 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-full font-bold text-sm transition-all"
                >
                  Get Started
                </a>
              </div>
            </div>
          </div>
          
          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-zinc-500 text-sm">
              © 2025 SmartKick. All rights reserved.
            </p>
            <p className="text-zinc-600 text-xs">
              Made in the UK 🇬🇧
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
